import { useState, useEffect } from 'react';
import { getOrders, getSuppliers, getOrdersBySupplier, getTewariBox, getCashSummary, getNextDeliveryDate, getPrevDeliveryDate, getRouteOrder, getProducts } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Printer, FileText, Ticket, MessageSquare, MapPin, Users, Copy, Archive, Package, Fish } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const PrintOrders = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [allSupplierOrders, setAllSupplierOrders] = useState({}); // 全仕入れ先の注文データ
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [activeTab, setActiveTab] = useState('customer');
  const [loadingAllSuppliers, setLoadingAllSuppliers] = useState(false);
  const [tewariData, setTewariData] = useState({ decisions: [], stock_orders: [] });
  const [cashSummary, setCashSummary] = useState({ unpaid_items: [], paid_items: [] });
  const [routeOrderList, setRouteOrderList] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => { fetchData(); fetchRouteOrder(); }, []);

  useEffect(() => {
    if (selectedSupplier) {
      fetchSupplierOrders();
    }
  }, [selectedSupplier, selectedDate]);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, productsRes] = await Promise.all([getOrders(), getSuppliers(), getProducts()]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setAllProducts(productsRes.data || []);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchRouteOrder = async () => {
    try {
      const res = await getRouteOrder();
      setRouteOrderList(res.data.routes || []);
    } catch (error) {}
  };

  const fetchSupplierOrders = async () => {
    try {
      const res = await getOrdersBySupplier(selectedSupplier, selectedDate);
      setSupplierOrders(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  // 全仕入れ先の注文を一括取得
  const fetchAllSupplierOrders = async () => {
    setLoadingAllSuppliers(true);
    try {
      const results = {};
      await Promise.all(
        suppliers.map(async (supplier) => {
          try {
            const res = await getOrdersBySupplier(supplier.id, selectedDate);
            if (res.data && res.data.length > 0) {
              results[supplier.id] = res.data;
            }
          } catch (e) {
            console.error(`Error fetching orders for supplier ${supplier.id}:`, e);
          }
        })
      );
      setAllSupplierOrders(results);
    } catch (error) {
      toast.error('仕入れ先データの取得に失敗しました');
    } finally {
      setLoadingAllSuppliers(false);
    }
  };

  // 仕入れ先タブに切り替えた時、または日付が変更された時に全仕入れ先データを取得
  useEffect(() => {
    if (activeTab === 'supplier' && suppliers.length > 0) {
      fetchAllSupplierOrders();
    }
  }, [activeTab, selectedDate, suppliers]);

  // 在庫対応タブ: tewariデータ取得
  useEffect(() => {
    if (activeTab === 'stock' && selectedDate) {
      getTewariBox(selectedDate).then(res => setTewariData(res.data)).catch(() => {});
    }
  }, [activeTab, selectedDate]);

  // 豊洲タスクリスト: 発注データ＋支払いデータを取得
  // 支払いデータは前営業日の登録分を取得（土曜に登録→月曜に支払い）
  const [toyosuLastFetched, setToyosuLastFetched] = useState(null);

  const refreshToyosuData = async () => {
    if (!selectedDate || suppliers.length === 0) return;
    await fetchAllSupplierOrders();
    try {
      const prevRes = await getPrevDeliveryDate(selectedDate);
      const prevDate = prevRes.data.prev_delivery_date;
      const res = await getCashSummary(prevDate);
      setCashSummary(res.data);
      setToyosuLastFetched(new Date());
    } catch (e) {
      console.error('cash-summary fetch error', e);
    }
  };

  useEffect(() => {
    if (activeTab === 'toyosu' && selectedDate && suppliers.length > 0) {
      refreshToyosuData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDate, suppliers]);

  // ウィンドウフォーカス復帰時にも自動再取得（タブ切替で戻ってきた瞬間に最新化）
  useEffect(() => {
    if (activeTab !== 'toyosu') return;
    const handleFocus = () => { refreshToyosuData(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') handleFocus();
    });
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDate, suppliers]);

  const filteredOrders = selectedDate 
    ? orders.filter(o => o.delivery_date === selectedDate && o.status !== 'cancelled')
    : orders.filter(o => o.status !== 'cancelled');

  // お米商品IDセット
  const riceProductIds = new Set(allProducts.filter(p => p.print_group === 'お米').map(p => p.id));
  
  // 通常帳票用: お米商品を除外した注文
  const regularOrders = filteredOrders.map(o => ({
    ...o,
    items: (o.items || []).filter(item => !riceProductIds.has(item.item_id))
  })).filter(o => o.items.length > 0);
  
  // お米帳票用: お米商品のみの注文
  const riceOrders = filteredOrders.map(o => ({
    ...o,
    items: (o.items || []).filter(item => riceProductIds.has(item.item_id))
  })).filter(o => o.items.length > 0);

  const handlePrint = async () => {
    // 豊洲タスクの印刷時は最新の支払いデータを取得（時間差ズレ防止）
    if (activeTab === 'toyosu' && selectedDate) {
      try {
        await refreshToyosuData();
        // React の state 反映を待ってから印刷
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (e) {
        console.error('豊洲タスク 最新データ取得失敗:', e);
      }
    } else if (activeTab === 'supplier' && selectedDate) {
      try { await fetchAllSupplierOrders(); await new Promise(resolve => setTimeout(resolve, 200)); } catch (e) { /* noop */ }
    }
    window.print();
  };

  // 仕入れ先別発注書: 同一顧客・同一商品・同一配送日の注文を集計
  const aggregateSupplierOrders = (items) => {
    const aggregated = {};
    items.forEach(item => {
      const key = `${item.customer_code}-${item.item_name}-${item.origin || ''}-${item.spec || ''}`;
      if (!aggregated[key]) {
        aggregated[key] = { ...item, quantity: 0 };
      }
      aggregated[key].quantity += item.quantity;
    });
    return Object.values(aggregated).sort((a, b) => {
      if (a.customer_code !== b.customer_code) {
        return (a.customer_code || '').localeCompare(b.customer_code || '');
      }
      return (a.item_name || '').localeCompare(b.item_name || '');
    });
  };

  // ピッキングリスト用: 同一商品を合算（商品名+産地+規格で集計）
  const aggregateItems = (items) => {
    const aggregated = {};
    items.forEach(item => {
      const key = `${item.item_name}-${item.origin || ''}-${item.spec || ''}`;
      if (!aggregated[key]) {
        aggregated[key] = { ...item, quantity: 0, subtotal: 0 };
      }
      aggregated[key].quantity += item.quantity;
      aggregated[key].subtotal += item.subtotal || 0;
    });
    return Object.values(aggregated).sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  // 顧客別データの準備（商品は合算）
  // 同一顧客の通常注文＋代理発注を統合: customer_code 優先（代理発注も同じコード）、なければ customer_id/customer_name
  const customerGroups = (() => {
    const grouped = regularOrders.reduce((acc, order) => {
      // グループキー: customer_code を最優先、なければ customer_id、最後に customer_name
      const code = (order.customer_code || '').trim();
      const groupKey = code
        ? `code-${code}`
        : (order.customer_id ? `id-${order.customer_id}` : `name-${order.customer_name || ''}`);
      
      if (!acc[groupKey]) {
        acc[groupKey] = {
          customer_id: order.customer_id,
          customer_name: order.customer_name,
          customer_code: order.customer_code,
          route_number: order.route_number,
          items: [],
          notes: [],
          total: 0,
          coupon_name: null,
          coupon_discount: 0,
          has_proxy: false,
        };
      }
      if (order.is_proxy_order) {
        acc[groupKey].has_proxy = true;
      }
      // ルート番号は土曜ルートを優先反映（後から来た注文で上書きしすぎないよう、空文字なら既存維持）
      if (order.route_number && !acc[groupKey].route_number) {
        acc[groupKey].route_number = order.route_number;
      }
      // 全アイテムを追加
      (order.items || []).forEach(item => {
        acc[groupKey].items.push(item);
        acc[groupKey].total += item.subtotal || 0;
      });
      if (order.notes) {
        acc[groupKey].notes.push(order.notes);
      }
      if (order.coupon_name) {
        acc[groupKey].coupon_name = order.coupon_name;
        acc[groupKey].coupon_discount += order.coupon_discount || 0;
      }
      return acc;
    }, {});
    
    // 各顧客の商品を合算
    Object.values(grouped).forEach(group => {
      group.items = aggregateItems(group.items);
    });
    
    return Object.values(grouped);
  })();

  // 新しいピッキングリスト用データ（ページ分割）
  const pickingListPages = (() => {
    const pages = [];
    
    // 1. 「とさか」を含む顧客をまとめる
    const tosakaCustomers = customerGroups.filter(g => 
      (g.customer_name || '').includes('とさか')
    );
    
    // 2. その他の顧客
    const otherCustomers = customerGroups.filter(g => 
      !(g.customer_name || '').includes('とさか')
    );
    
    // ルート表示順マップ
    const routeOrderMap = {};
    routeOrderList.forEach((r, idx) => { routeOrderMap[r] = idx; });

    // 列優先に並び替え（左上→下→次列の上から下）
    const reorderColumnFirst = (items, numCols = 3) => {
      const numRows = Math.ceil(items.length / numCols);
      const reordered = [];
      for (let row = 0; row < numRows; row++) {
        for (let col = 0; col < numCols; col++) {
          const idx = col * numRows + row;
          if (idx < items.length) {
            reordered.push(items[idx]);
          }
        }
      }
      return reordered;
    };

    // とさかグループをルート表示順でソートして追加
    if (tosakaCustomers.length > 0) {
      const sortedTosaka = [...tosakaCustomers].sort((a, b) => {
        const aRoute = a.route_number || '';
        const bRoute = b.route_number || '';
        const aOrder = routeOrderMap[aRoute] !== undefined ? routeOrderMap[aRoute] : 99999;
        const bOrder = routeOrderMap[bRoute] !== undefined ? routeOrderMap[bRoute] : 99999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return aRoute.localeCompare(bRoute);
      });
      // 9件ずつページ分割＋列優先並び替え
      for (let i = 0; i < sortedTosaka.length; i += 9) {
        const pageCustomers = sortedTosaka.slice(i, i + 9);
        if (pageCustomers.length > 0) {
          const pageNum = Math.floor(i / 9) + 1;
          pages.push({
            title: sortedTosaka.length > 9 ? `とさかグループ (${pageNum})` : 'とさかグループ',
            customers: reorderColumnFirst(pageCustomers)
          });
        }
      }
    }
    
    // その他はマスタのルート表示順でソートして9件ずつページに分割
    const sortedOthers = [...otherCustomers].sort((a, b) => {
      const aRoute = a.route_number || '';
      const bRoute = b.route_number || '';
      const aOrder = routeOrderMap[aRoute] !== undefined ? routeOrderMap[aRoute] : 99999;
      const bOrder = routeOrderMap[bRoute] !== undefined ? routeOrderMap[bRoute] : 99999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return aRoute.localeCompare(bRoute);
    });
    
    // 9件ずつページに分割し、各ページ内を列優先に並び替え
    for (let i = 0; i < sortedOthers.length; i += 9) {
      const pageCustomers = sortedOthers.slice(i, i + 9);
      if (pageCustomers.length > 0) {
        const pageNum = Math.floor(i / 9) + 1;
        pages.push({
          title: `その他 (${pageNum})`,
          customers: reorderColumnFirst(pageCustomers)
        });
      }
    }
    
    return pages;
  })();

  // コメント一覧データ
  const ordersWithComments = regularOrders.filter(o => o.notes && o.notes.trim() !== '');

  return (
    <div className="space-y-6 fade-in" data-testid="print-orders-page">
      <div className="page-header no-print">
        <h1 className="page-title">印刷用帳票</h1>
        <Button onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          印刷
        </Button>
      </div>

      <div className="no-print">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="customer">顧客別注文一覧</TabsTrigger>
            <TabsTrigger value="route">ルート別一覧</TabsTrigger>
            <TabsTrigger value="supplier">仕入れ先別注文票</TabsTrigger>
            <TabsTrigger value="stock">在庫対応</TabsTrigger>
            <TabsTrigger value="toyosu">豊洲タスクリスト</TabsTrigger>
            <TabsTrigger value="comments">コメント確認</TabsTrigger>
            <TabsTrigger value="rice" className="bg-amber-50 text-amber-800 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-900 border border-amber-300 ml-2">お米</TabsTrigger>
          </TabsList>

          <TabsContent value="customer" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="route" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="supplier" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                  {loadingAllSuppliers && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
                      <span className="text-sm">読み込み中...</span>
                    </div>
                  )}
                  {!loadingAllSuppliers && Object.keys(allSupplierOrders).length > 0 && (
                    <Badge className="bg-green-100 text-green-800">
                      {Object.keys(allSupplierOrders).length}社の発注書
                    </Badge>
                  )}
                </div>
              </CardHeader>
              {/* LINE仕入れ先のコピー */}
              {!loadingAllSuppliers && Object.keys(allSupplierOrders).length > 0 && (
                <CardContent>
                  {(() => {
                    const lineSuppliers = Object.entries(allSupplierOrders).filter(([sid]) => {
                      const s = suppliers.find(x => x.id === sid);
                      return s && s.order_method === 'LINE';
                    });
                    if (lineSuppliers.length === 0) return null;
                    return (
                      <>
                        <p className="text-xs text-slate-500 mb-2">LINE発注用テキストをコピーできます</p>
                        <div className="space-y-2">
                          {lineSuppliers.map(([supplierId, items]) => {
                            const supplier = suppliers.find(s => s.id === supplierId);
                            const itemsList = Array.isArray(items) ? items : [];
                            const dateStr = format(new Date(selectedDate), 'M/d(E)', { locale: ja });
                            
                            // 顧客軸でグループ化
                            const byCustomer = {};
                            itemsList.forEach(item => {
                              const custName = item.customer_name || '不明';
                              if (!byCustomer[custName]) byCustomer[custName] = [];
                              byCustomer[custName].push(item);
                            });
                            
                            let lineText = `おはようございます。\n${dateStr}引取分の発注です。\n`;
                            Object.entries(byCustomer).forEach(([custName, custItems]) => {
                              lineText += `\n■ ${custName}\n`;
                              custItems.forEach(item => {
                                const spec = item.spec || '';
                                lineText += `  ${item.item_name}${spec ? ` ${spec}` : ''} x${item.quantity}\n`;
                              });
                            });
                            lineText += `\nお願いします。`;
                            return (
                              <div key={supplierId} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs px-2 py-0.5 rounded font-medium bg-green-100 text-green-700">LINE</span>
                                  <span className="font-medium text-sm">{supplier.name}</span>
                                  <span className="text-xs text-slate-500">{itemsList.length}品</span>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(lineText); toast.success(`${supplier.name}の発注文をコピーしました`); }}>
                                  <Copy className="h-4 w-4 mr-1" />コピー
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              )}
            </Card>
          </TabsContent>


          <TabsContent value="stock" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" />
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const stockItems = (tewariData.decisions || []).filter(d => d.decision === '在庫対応');
                  const stockOrdersList = tewariData.stock_orders || [];
                  if (stockItems.length === 0 && stockOrdersList.length === 0) {
                    return <p className="text-center text-slate-400 py-6">在庫対応のデータはありません</p>;
                  }
                  return (
                    <div className="space-y-4">
                      {stockItems.length > 0 && (
                        <div>
                          <h3 className="font-bold text-sm mb-2 flex items-center gap-2"><Archive className="h-4 w-4" />在庫対応（在庫から出荷）</h3>
                          <table className="w-full text-sm border-collapse">
                            <thead><tr className="border-b text-left text-slate-500"><th className="py-2 px-3">商品名</th><th className="py-2 px-3">産地/規格</th><th className="py-2 px-3">顧客</th><th className="py-2 px-3 text-right">数量</th></tr></thead>
                            <tbody>
                              {stockItems.map((item, idx) => (
                                <tr key={idx} className="border-b border-slate-100">
                                  <td className="py-2 px-3 font-medium">{item.item_name}</td>
                                  <td className="py-2 px-3 text-slate-500">{[item.origin, item.spec].filter(Boolean).join('/') || '-'}</td>
                                  <td className="py-2 px-3">{item.customer_name}{item.customer_code ? ` (${item.customer_code})` : ''}</td>
                                  <td className="py-2 px-3 text-right font-mono font-bold">{item.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {stockOrdersList.length > 0 && (
                        <div>
                          <h3 className="font-bold text-sm mb-2 flex items-center gap-2 text-purple-700"><Package className="h-4 w-4" />在庫用発注</h3>
                          <table className="w-full text-sm border-collapse">
                            <thead><tr className="border-b text-left text-slate-500"><th className="py-2 px-3">商品名</th><th className="py-2 px-3">産地/規格</th><th className="py-2 px-3">仕入れ先</th><th className="py-2 px-3 text-right">数量</th></tr></thead>
                            <tbody>
                              {stockOrdersList.map((item, idx) => (
                                <tr key={idx} className="border-b border-slate-100 bg-purple-50">
                                  <td className="py-2 px-3 font-medium">{item.item_name}</td>
                                  <td className="py-2 px-3 text-slate-500">{[item.origin, item.spec].filter(Boolean).join('/') || '-'}</td>
                                  <td className="py-2 px-3">{item.supplier_name || '-'}</td>
                                  <td className="py-2 px-3 text-right font-mono font-bold">{item.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="toyosu" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshToyosuData}
                    data-testid="toyosu-refresh-btn"
                  >
                    最新化
                  </Button>
                  {toyosuLastFetched && (
                    <span className="text-xs text-slate-500" data-testid="toyosu-last-fetched">
                      最終取得: {format(toyosuLastFetched, 'HH:mm:ss', { locale: ja })}
                    </span>
                  )}
                  {loadingAllSuppliers && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
                      <span className="text-sm">読み込み中...</span>
                    </div>
                  )}
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="rice" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                  <Badge variant="outline" className="text-sm px-3 py-1">
                    お米注文: {riceOrders.length}件
                  </Badge>
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="comments" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg">配送日を選択</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                </div>
              </CardHeader>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* 印刷用: 顧客別注文一覧 - ページ圧縮版（とさか1ページ、その他12ルートごと） */}
      {activeTab === 'customer' && (
        <div className="print-area" id="customer-orders-print">
          {regularOrders.length === 0 ? (
            <p className="text-center text-slate-500 py-8">該当する注文がありません</p>
          ) : (
            pickingListPages.map((page, pageIdx) => (
              <div key={pageIdx} className="picking-page">
                {/* ページヘッダー */}
                <div className="picking-header mb-4 border-b-2 border-black pb-2">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold">ピッキングリスト - {page.title}</h2>
                    <div className="text-right">
                      <p className="text-base">配送日: {format(new Date(selectedDate), 'yyyy年M月d日(E)', { locale: ja })}</p>
                      <p className="text-sm">ページ {pageIdx + 1} / {pickingListPages.length}</p>
                    </div>
                  </div>
                </div>

                {/* 顧客ごとにボックス表示 */}
                <div className="picking-customers">
                  {page.customers.map((customer, custIdx) => (
                    <div key={custIdx} className="customer-box">
                      {/* 顧客ヘッダー - 白黒印刷対応 */}
                      <div className="customer-box-header">
                        <span className="route-badge">{customer.route_number || '-'}</span>
                        <span className="customer-code">{customer.customer_code || '-'}</span>
                        <span className="customer-name">{customer.customer_name}</span>
                        {customer.has_proxy && (
                          <span className="proxy-indicator">代理発注分含む</span>
                        )}
                        {customer.notes && customer.notes.length > 0 && customer.notes.some(n => n && n.trim()) && (
                          <span className="comment-indicator">※コメントあり</span>
                        )}
                      </div>
                      {/* 商品リスト */}
                      <div className="customer-box-items">
                        {customer.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="item-row">
                            <span className="item-name">{item.item_name}</span>
                            <span className="item-spec">{item.spec || '-'}</span>
                            <span className="item-qty">{item.quantity}</span>
                            <span className="item-check">☐</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            ))
          )}
        </div>
      )}

      {/* 印刷用: 仕入れ先別注文票 - 各仕入れ先が1ページに収まるコンパクト表示 */}
      {activeTab === 'supplier' && (
        <div className="print-area" id="supplier-orders-print">
          {loadingAllSuppliers ? (
            <div className="text-center py-12 text-slate-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-3"></div>
              <p>データを読み込み中...</p>
            </div>
          ) : Object.keys(allSupplierOrders).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>該当する注文がありません</p>
            </div>
          ) : (
            // 各仕入れ先ごとに1ページ（コンパクト）
            // ソート: FAX→LINE→送信なし、同じ発注手段内は 届→引→買
            Object.entries(allSupplierOrders)
            .map(([supplierId, orders]) => ({ supplierId, orders, supplier: suppliers.find(s => s.id === supplierId) }))
            .filter(({ supplier, orders }) => supplier && orders && orders.length > 0)
            .sort((a, b) => {
              const methodOrder = { 'FAX': 1, 'LINE': 2, '': 3 };
              const deliveryOrder = { '届': 1, '引': 2, '買': 3, '': 4 };
              const aMethod = methodOrder[a.supplier.order_method || ''] || 3;
              const bMethod = methodOrder[b.supplier.order_method || ''] || 3;
              if (aMethod !== bMethod) return aMethod - bMethod;
              const aDelivery = deliveryOrder[a.supplier.delivery_type || ''] || 4;
              const bDelivery = deliveryOrder[b.supplier.delivery_type || ''] || 4;
              if (aDelivery !== bDelivery) return aDelivery - bDelivery;
              return (a.supplier.name || '').localeCompare(b.supplier.name || '');
            })
            .map(({ supplierId, orders, supplier }) => {
              
              // 商品別集計
              const productSummary = Object.values(orders.reduce((acc, item) => {
                const key = `${item.item_name}-${item.origin}-${item.spec}`;
                if (!acc[key]) {
                  acc[key] = { ...item, quantity: 0 };
                }
                acc[key].quantity += item.quantity;
                return acc;
              }, {}));
              
              return (
                <div key={supplierId} className="supplier-page-compact">
                  {/* 発注書コンテンツ部分 */}
                  <div className="supplier-content">
                    {/* 発注書ヘッダー */}
                    <div className="supplier-header">
                      <h2 className="supplier-title">発 注 書<span className="order-method-label">【{supplier.order_method || '送信なし'}】</span></h2>
                      <p className="supplier-date">引き取り希望日：{format(new Date(selectedDate), 'yyyy年M月d日（E）', { locale: ja })}</p>
                      <p className="supplier-name">{supplier.name}　御中</p>
                    </div>

                    {/* 商品別集計 */}
                    <div className="supplier-section">
                      <h3 className="section-label">商品別集計</h3>
                      <table className="w-full border-collapse supplier-table">
                        <thead>
                          <tr>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm">商品名</th>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm col-origin-spec">産地/規格</th>
                            <th className="border border-slate-400 px-2 py-1 text-right text-sm w-24">合計</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productSummary.map((item, idx) => (
                            <tr key={idx}>
                              <td className="border border-slate-300 px-2 py-1 text-sm">{item.item_name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm col-origin-spec">
                                {[item.origin, item.spec].filter(Boolean).join('/') || '-'}
                              </td>
                              <td className="border border-slate-300 px-2 py-1 text-sm text-right font-bold text-blue-700">
                                {item.quantity}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-1 text-right text-sm text-slate-500">
                        商品: {productSummary.length}種
                      </div>
                    </div>

                    {/* メモ欄 */}
                    <div className="memo-section">
                      <h3 className="section-label">メモ欄</h3>
                      <div className="memo-lines border border-slate-300">
                        {[...Array(5)].map((_, idx) => (
                          <div key={idx} className="memo-line border-b border-slate-200 h-6"></div>
                        ))}
                      </div>
                    </div>

                    {/* 配送先別明細 */}
                    <div className="supplier-section">
                      <h3 className="section-label">配送先別明細</h3>
                      <table className="w-full border-collapse supplier-table">
                        <thead>
                          <tr>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm">配送先</th>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm">商品名</th>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm col-origin-spec">産地/規格</th>
                            <th className="border border-slate-400 px-2 py-1 text-right text-sm w-20">数量</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aggregateSupplierOrders(orders).map((item, idx) => (
                            <tr key={idx}>
                              <td className="border border-slate-300 px-2 py-1 text-sm truncate max-w-[140px]">{item.customer_name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm">{item.item_name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm col-origin-spec">
                                {[item.origin, item.spec].filter(Boolean).join('/') || '-'}
                              </td>
                              <td className="border border-slate-300 px-2 py-1 text-sm text-right font-bold">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-1 text-right text-sm text-slate-500">
                        配送先: {new Set(orders.map(o => o.customer_name || o.customer_code)).size}件
                      </div>
                    </div>
                  </div>

                  {/* フッター: ページ下部に固定 */}
                  <div className="supplier-footer">
                    <div className="supplier-footer-left">
                      <p>野菜の丸平 / 株式会社フレッシュエージェント</p>
                      <p>センター直通携帯 080-3485-8420 伊藤まで</p>
                      <p>※不明な内容等ありましたらご連絡下さい。</p>
                    </div>
                    {supplier.delivery_type && (
                      <div className="delivery-type-mark">
                        {supplier.delivery_type}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* 印刷用: ルート別一覧 */}
      {activeTab === 'route' && (
        <div className="print-area" id="route-orders-print">
          <div className="print-header text-center mb-4">
            <h2 className="text-lg font-bold">ルート別配送一覧</h2>
            <p className="text-xs">配送日: {format(new Date(selectedDate), 'yyyy年MM月dd日(E)', { locale: ja })}</p>
          </div>

          {regularOrders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>該当する注文がありません</p>
            </div>
          ) : (
            <div className="route-cards-grid">
              {(() => {
                // ルートごとにグループ化（同一顧客の通常注文と代理発注を統合）
                const routeGroups = regularOrders.reduce((acc, order) => {
                  const route = order.route_number || '未設定';
                  if (!acc[route]) acc[route] = [];
                  const customerKey = (order.customer_code || '').trim() || order.customer_id || order.customer_name;
                  if (!acc[route].some(o => ((o.customer_code || '').trim() || o.customer_id || o.customer_name) === customerKey)) {
                    acc[route].push(order);
                  }
                  return acc;
                }, {});

                // ルート番号でソート
                const sortedRoutes = Object.keys(routeGroups).sort((a, b) => {
                  if (a === '未設定') return 1;
                  if (b === '未設定') return -1;
                  return a.localeCompare(b, 'ja', { numeric: true });
                });

                return sortedRoutes.map((route) => (
                  <div key={route} className="route-card">
                    <div className="route-card-header">
                      <span className="route-title">ルート {route}</span>
                      <span className="route-count">{routeGroups[route].length}件</span>
                    </div>
                    <div className="route-card-body">
                      {routeGroups[route].map((order, idx) => (
                        <div key={idx} className="route-customer">
                          <span className="customer-name">{order.customer_name || '-'}</span>
                          {order.customer_code && (
                            <span className="customer-code">{order.customer_code}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* 集計サマリー */}
          {regularOrders.length > 0 && (
            <div className="route-summary">
              配送先合計: {new Set(regularOrders.map(o => o.customer_name || o.customer_id)).size}件
            </div>
          )}
        </div>
      )}

      {/* 印刷用: 在庫対応 */}
      {activeTab === 'stock' && (
        <div className="print-area" id="stock-print">
          <div className="print-header text-center mb-6">
            <h2 className="text-2xl font-bold">在庫対応一覧</h2>
            <p className="text-lg mt-2">配送日: {format(new Date(selectedDate), 'yyyy年MM月dd日(E)', { locale: ja })}</p>
          </div>

          {(() => {
            const stockItems = (tewariData.decisions || []).filter(d => d.decision === '在庫対応');
            const stockOrdersList = tewariData.stock_orders || [];
            if (stockItems.length === 0 && stockOrdersList.length === 0) {
              return <p className="text-center py-8">在庫対応のデータはありません</p>;
            }
            return (
              <div className="stock-print-content">
                {stockItems.length > 0 && (
                  <div className="stock-section">
                    <h3 className="section-label">在庫対応（在庫から出荷）</h3>
                    <table className="w-full border-collapse supplier-table">
                      <thead>
                        <tr>
                          <th className="border border-slate-400 px-3 py-2 text-left">商品名</th>
                          <th className="border border-slate-400 px-3 py-2 text-left">産地/規格</th>
                          <th className="border border-slate-400 px-3 py-2 text-left">顧客</th>
                          <th className="border border-slate-400 px-3 py-2 text-center w-24">数量</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockItems.map((item, idx) => (
                          <tr key={idx}>
                            <td className="border border-slate-300 px-3 py-2 font-medium">{item.item_name}</td>
                            <td className="border border-slate-300 px-3 py-2">{[item.origin, item.spec].filter(Boolean).join('/') || '-'}</td>
                            <td className="border border-slate-300 px-3 py-2">{item.customer_name}{item.customer_code ? ` (${item.customer_code})` : ''}</td>
                            <td className="border border-slate-300 px-3 py-2 text-center font-bold">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {stockOrdersList.length > 0 && (
                  <div className="stock-section" style={{marginTop: '8mm'}}>
                    <h3 className="section-label">在庫用発注</h3>
                    <table className="w-full border-collapse supplier-table">
                      <thead>
                        <tr>
                          <th className="border border-slate-400 px-3 py-2 text-left">商品名</th>
                          <th className="border border-slate-400 px-3 py-2 text-left">産地/規格</th>
                          <th className="border border-slate-400 px-3 py-2 text-left">仕入れ先</th>
                          <th className="border border-slate-400 px-3 py-2 text-center w-24">数量</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockOrdersList.map((item, idx) => (
                          <tr key={idx}>
                            <td className="border border-slate-300 px-3 py-2 font-medium">{item.item_name}</td>
                            <td className="border border-slate-300 px-3 py-2">{[item.origin, item.spec].filter(Boolean).join('/') || '-'}</td>
                            <td className="border border-slate-300 px-3 py-2">{item.supplier_name || '-'}</td>
                            <td className="border border-slate-300 px-3 py-2 text-center font-bold">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* 印刷用: 豊洲リスト */}
      {activeTab === 'toyosu' && (
        <div className="print-area" id="toyosu-print">
          <div className="print-header text-center mb-6">
            <h2 className="text-2xl font-bold">豊洲タスクリスト</h2>
            <p className="text-lg mt-2">{format(new Date(selectedDate), 'yyyy年M月d日（E）', { locale: ja })}</p>
          </div>

          {(() => {
            // 発注データから仕入れ先リストを作成
            const supplierTaskList = [];
            const addedSupplierNames = new Set();

            // 週払いで「支払い済み」チェック済みの仕入れ先ID集合（豊洲タスクから除外）
            const paidWeeklySupplierIds = new Set(
              (cashSummary.weekly_items || []).filter(w => w.paid).map(w => w.supplier_id)
            );

            // ヘルパー: 仕入れ先の支払い金額を計算
            const getPaymentInfo = (supplierName, supplierId) => {
              const cashAmount = (cashSummary.unpaid_items || [])
                .filter(i => i.supplier_name === supplierName)
                .reduce((sum, i) => sum + (i.amount || 0), 0);
              // 週払いで支払い済みの仕入れ先は credit からも除外
              const isWeeklyPaid = supplierId && paidWeeklySupplierIds.has(supplierId);
              const creditAmount = isWeeklyPaid ? 0 : (cashSummary.credit_unpaid_items || [])
                .filter(i => i.supplier_name === supplierName)
                .reduce((sum, i) => sum + (i.amount || 0), 0);
              let paymentLabel = '-';
              if (cashAmount > 0 && creditAmount > 0) {
                paymentLabel = `現金¥${cashAmount.toLocaleString()} + 掛け¥${creditAmount.toLocaleString()}`;
              } else if (cashAmount > 0) {
                paymentLabel = `現金 ¥${cashAmount.toLocaleString()}`;
              } else if (creditAmount > 0) {
                paymentLabel = `掛け ¥${creditAmount.toLocaleString()}`;
              }
              return { cashAmount, creditAmount, total: cashAmount + creditAmount, paymentLabel, isWeeklyPaid };
            };

            // 1. 発注がある仕入れ先のタスクを生成
            Object.entries(allSupplierOrders).forEach(([supplierId, orders]) => {
              const supplier = suppliers.find(s => s.id === supplierId);
              if (!supplier || !orders || orders.length === 0) return;

              const orderMethod = supplier.order_method || '';
              let orderStatus = '未発注';
              if (orderMethod === 'FAX') orderStatus = 'FAX注文済み';
              else if (orderMethod === 'LINE') orderStatus = 'LINE注文済み';

              const deliveryType = supplier.delivery_type || '';
              let taskType = '';
              if (deliveryType === '引') taskType = '荷物引き取り';
              else if (deliveryType === '届') taskType = '届け荷確認';
              else if (deliveryType === '買') taskType = '買い物';

              const payment = getPaymentInfo(supplier.name, supplier.id);

              supplierTaskList.push({
                name: supplier.name,
                orderStatus,
                taskType,
                hasPayment: payment.total > 0,
                paymentLabel: payment.paymentLabel,
              });
              addedSupplierNames.add(supplier.name);
            });

            // 2. 発注がないが現金支払いがある仕入れ先を追加
            (cashSummary.unpaid_items || []).forEach(item => {
              if (!addedSupplierNames.has(item.supplier_name)) {
                const payment = getPaymentInfo(item.supplier_name);
                supplierTaskList.push({
                  name: item.supplier_name,
                  orderStatus: '-',
                  taskType: '支払い',
                  hasPayment: true,
                  paymentLabel: payment.paymentLabel,
                });
                addedSupplierNames.add(item.supplier_name);
              }
            });

            // 3. 発注がないが掛け支払いがある仕入れ先を追加
            (cashSummary.credit_unpaid_items || []).forEach(item => {
              // 週払い済みフラグの仕入れ先は除外
              if (item.supplier_id && paidWeeklySupplierIds.has(item.supplier_id)) return;
              if (!addedSupplierNames.has(item.supplier_name)) {
                const payment = getPaymentInfo(item.supplier_name, item.supplier_id);
                supplierTaskList.push({
                  name: item.supplier_name,
                  orderStatus: '-',
                  taskType: '支払い',
                  hasPayment: true,
                  paymentLabel: payment.paymentLabel,
                });
                addedSupplierNames.add(item.supplier_name);
              }
            });

            // イレギュラー支払いを仕入れ先行に統合、または新規行として追加
            const irregularItems = cashSummary.irregular_items || [];
            irregularItems.forEach(item => {
              if (item.supplier_id && item.supplier_name !== 'その他') {
                // 既存の仕入れ先行に支払い情報を追加
                const existing = supplierTaskList.find(s => s.name === item.supplier_name);
                if (existing) {
                  const prev = existing.paymentLabel === '-' ? 0 : 0;
                  existing.paymentLabel = existing.paymentLabel === '-'
                    ? `イレギュラー ¥${(item.amount || 0).toLocaleString()}`
                    : `${existing.paymentLabel} + IRR¥${(item.amount || 0).toLocaleString()}`;
                  existing.hasPayment = true;
                } else {
                  supplierTaskList.push({
                    name: item.supplier_name,
                    orderStatus: '-',
                    taskType: '',
                    hasPayment: true,
                    paymentLabel: `イレギュラー ¥${(item.amount || 0).toLocaleString()}`,
                    itemCount: 0,
                  });
                }
              } else {
                // その他のイレギュラー
                supplierTaskList.push({
                  name: item.name || 'その他',
                  orderStatus: '-',
                  taskType: '',
                  hasPayment: true,
                  paymentLabel: `イレギュラー ¥${(item.amount || 0).toLocaleString()}`,
                  itemCount: 0,
                });
              }
            });

            // 週払いを仕入れ先行に統合
            const weeklyItems = (cashSummary.weekly_items || []).filter(w => w.amount > 0);
            weeklyItems.forEach(item => {
              const existing = supplierTaskList.find(s => s.name === item.supplier_name);
              if (existing) {
                existing.paymentLabel = existing.paymentLabel === '-'
                  ? `週払い ¥${(item.amount || 0).toLocaleString()}`
                  : `${existing.paymentLabel} + 週払¥${(item.amount || 0).toLocaleString()}`;
                existing.hasPayment = true;
              } else {
                supplierTaskList.push({
                  name: item.supplier_name,
                  orderStatus: '-',
                  taskType: '',
                  hasPayment: true,
                  paymentLabel: `週払い ¥${(item.amount || 0).toLocaleString()}`,
                  itemCount: 0,
                });
              }
            });

            if (supplierTaskList.length === 0) {
              return <p className="text-center py-8">該当するデータがありません</p>;
            }

            // タスク種別でソート（荷物引き取り → 届け荷確認 → 買い物 → 未設定）
            const taskOrder = { '荷物引き取り': 1, '届け荷確認': 2, '買い物': 3, '支払い': 4, '': 5 };
            supplierTaskList.sort((a, b) => (taskOrder[a.taskType] || 4) - (taskOrder[b.taskType] || 4));

            return (
              <table className="toyosu-table">
                <thead>
                  <tr>
                    <th className="toyosu-th">仕入れ先</th>
                    <th className="toyosu-th">発注状況</th>
                    <th className="toyosu-th">タスク</th>
                    <th className="toyosu-th">支払い</th>
                    <th className="toyosu-th toyosu-check">確認</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierTaskList.map((item, idx) => (
                    <tr key={idx} className="toyosu-row">
                      <td className="toyosu-td toyosu-name">{item.name}</td>
                      <td className="toyosu-td">{item.orderStatus}</td>
                      <td className="toyosu-td">{item.taskType || '-'}</td>
                      <td className="toyosu-td">{item.paymentLabel}</td>
                      <td className="toyosu-td toyosu-check-cell">☐</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
      )}

      {/* 印刷用: お米発注書（仕入れ先別発注書と同じ形式） */}
      {activeTab === 'rice' && (
        <div className="print-area" id="rice-print">
          {riceOrders.length === 0 ? (
            <p className="text-center py-8">お米の注文はありません</p>
          ) : (() => {
            // 仕入れ先別にグループ化
            const riceBySupplier = {};
            riceOrders.forEach(order => {
              (order.items || []).forEach(item => {
                const sid = item.supplier_id || 'unknown';
                if (!riceBySupplier[sid]) riceBySupplier[sid] = [];
                riceBySupplier[sid].push({
                  ...item,
                  customer_name: order.customer_name || '-',
                  customer_code: order.customer_code || '',
                  delivery_date: order.delivery_date,
                });
              });
            });

            return Object.entries(riceBySupplier).map(([supplierId, items]) => {
              const supplier = suppliers.find(s => s.id === supplierId);
              const supplierName = supplier?.name || items[0]?.supplier_name || '不明';
              const orderMethod = supplier?.order_method || '';

              // 商品別集計
              const productSummary = Object.values(items.reduce((acc, item) => {
                const key = `${item.item_name}-${item.origin || ''}-${item.spec || ''}`;
                if (!acc[key]) acc[key] = { ...item, quantity: 0 };
                acc[key].quantity += item.quantity;
                return acc;
              }, {})).sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));

              return (
                <div key={supplierId} className="supplier-page-compact">
                  <div className="supplier-content">
                    <div className="supplier-header">
                      <h2 className="supplier-title">お米 発 注 書<span className="order-method-label">【{orderMethod || '送信なし'}】</span></h2>
                      <p className="supplier-date">配送希望日：{format(new Date(selectedDate), 'yyyy年M月d日（E）', { locale: ja })}</p>
                      <p className="supplier-name">{supplierName}　御中</p>
                    </div>

                    <div className="supplier-section">
                      <h3 className="section-label">商品別集計</h3>
                      <table className="w-full border-collapse supplier-table">
                        <thead>
                          <tr>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm">商品名</th>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm col-origin-spec">産地/規格</th>
                            <th className="border border-slate-400 px-2 py-1 text-center text-sm w-20">単位</th>
                            <th className="border border-slate-400 px-2 py-1 text-right text-sm w-24">合計</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productSummary.map((item, idx) => (
                            <tr key={idx}>
                              <td className="border border-slate-300 px-2 py-1 text-sm">{item.item_name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm col-origin-spec">
                                {[item.origin, item.spec].filter(Boolean).join('/') || '-'}
                              </td>
                              <td className="border border-slate-300 px-2 py-1 text-sm text-center">{item.unit || '-'}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm text-right font-bold text-blue-700">
                                {item.quantity}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="memo-section">
                      <h3 className="section-label">メモ欄</h3>
                      <div className="memo-lines border border-slate-300">
                        {[...Array(5)].map((_, idx) => (
                          <div key={idx} className="memo-line border-b border-slate-200 h-6"></div>
                        ))}
                      </div>
                    </div>

                    <div className="supplier-section">
                      <h3 className="section-label">配送先別明細</h3>
                      <table className="w-full border-collapse supplier-table">
                        <thead>
                          <tr>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm">配送先</th>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm">商品名</th>
                            <th className="border border-slate-400 px-2 py-1 text-left text-sm col-origin-spec">産地/規格</th>
                            <th className="border border-slate-400 px-2 py-1 text-center text-sm w-16">単位</th>
                            <th className="border border-slate-400 px-2 py-1 text-right text-sm w-20">数量</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, idx) => (
                            <tr key={idx}>
                              <td className="border border-slate-300 px-2 py-1 text-sm">{item.customer_name}{item.customer_code ? ` (${item.customer_code})` : ''}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm">{item.item_name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm col-origin-spec">
                                {[item.origin, item.spec].filter(Boolean).join('/') || '-'}
                              </td>
                              <td className="border border-slate-300 px-2 py-1 text-sm text-center">{item.unit || '-'}</td>
                              <td className="border border-slate-300 px-2 py-1 text-sm text-right font-bold">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-1 text-right text-sm text-slate-500">
                        配送先: {new Set(items.map(i => i.customer_name)).size}件
                      </div>
                    </div>
                  </div>

                  <div className="supplier-footer">
                    <div className="supplier-footer-left">
                      <p>野菜の丸平 / 株式会社フレッシュエージェント</p>
                      <p>センター直通携帯 080-3485-8420 伊藤まで</p>
                      <p>※不明な内容等ありましたらご連絡下さい。</p>
                    </div>
                    {supplier?.delivery_type && (
                      <div className="delivery-type-mark">
                        {supplier.delivery_type}
                      </div>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* 印刷用: コメント確認一覧 */}
      {activeTab === 'comments' && (
        <div className="print-area" id="comments-print">
          <div className="print-header text-center mb-6">
            <h2 className="text-2xl font-bold">申し送り事項一覧</h2>
            <p className="text-lg mt-2">配送日: {format(new Date(selectedDate), 'yyyy年MM月dd日(E)', { locale: ja })}</p>
          </div>

          {ordersWithComments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>該当する申し送り事項がありません</p>
            </div>
          ) : (
            <table className="w-full border-collapse border border-slate-300 text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-300 p-2 text-left w-24">コード</th>
                  <th className="border border-slate-300 p-2 text-left w-16">ルート</th>
                  <th className="border border-slate-300 p-2 text-left w-40">取引先名</th>
                  <th className="border border-slate-300 p-2 text-left">申し送り事項</th>
                  <th className="border border-slate-300 p-2 text-center w-16">確認</th>
                </tr>
              </thead>
              <tbody>
                {ordersWithComments.map((order, idx) => (
                  <tr key={order.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="border border-slate-300 p-2 font-mono">{order.customer_code || '-'}</td>
                    <td className="border border-slate-300 p-2">{order.route_number || '-'}</td>
                    <td className="border border-slate-300 p-2 font-medium">{order.customer_name}</td>
                    <td className="border border-slate-300 p-3">
                      <div className="bg-amber-50 border border-amber-200 rounded p-2">
                        {order.notes}
                      </div>
                    </td>
                    <td className="border border-slate-300 p-2 text-center">
                      <div className="w-6 h-6 border-2 border-slate-400 rounded mx-auto"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* コメント件数サマリー */}
          {ordersWithComments.length > 0 && (
            <div className="mt-4 text-right text-sm text-slate-600">
              申し送り件数: {ordersWithComments.length}件
            </div>
          )}
        </div>
      )}

      <style>{`
        @page {
          size: ${['customer', 'route'].includes(activeTab) ? 'landscape' : 'portrait'};
          margin: 10mm;
        }
        @media print {
          /* === 印刷時の全体リセット === */
          html, body {
            height: auto !important;
            width: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            zoom: 1 !important;
            -webkit-text-size-adjust: 100% !important;
            text-size-adjust: 100% !important;
            transform: none !important;
          }
          
          /* 全要素の背景を白に */
          * {
            background: transparent !important;
          }
          
          /* すべてのレイアウト制約を解除 */
          *, *::before, *::after {
            float: none !important;
          }
          
          /* Layoutラッパーの制約を完全解除 */
          [data-testid="app-layout"] {
            min-height: 0 !important;
            height: auto !important;
            display: block !important;
            overflow: visible !important;
          }
          [data-testid="app-layout"] > aside {
            display: none !important;
          }
          [data-testid="app-layout"] > main {
            margin: 0 !important;
            padding: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            display: block !important;
            overflow: visible !important;
          }
          [data-testid="app-layout"] > main > * {
            margin: 0 !important;
            padding: 0 !important;
          }
          /* パンくずリストを非表示 */
          [data-testid="app-layout"] > main > div.bg-white.border-b {
            display: none !important;
          }
          
          /* 非表示要素 */
          .no-print,
          aside, 
          header:not(.supplier-header), 
          nav,
          .page-header,
          [role="tablist"] {
            display: none !important;
          }
          
          /* print-area: 通常フロー */
          .print-area {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          [data-testid="print-orders-page"] {
            margin: 0 !important;
            padding: 0 !important;
            display: block !important;
            height: auto !important;
            min-height: 0 !important;
          }
          /* space-y-6 のマージンを消す */
          [data-testid="print-orders-page"] > * {
            margin-top: 0 !important;
          }
          
          /* 基本フォントサイズ（12pt以上） */
          body { font-size: 12pt !important; }
          table { font-size: 12pt !important; }
          
          /* ============================================ */
          /* ピッキングリスト（顧客別）                    */
          /* ============================================ */
          .picking-page {
            page-break-after: always;
            padding: 3mm;
          }
          .picking-page:last-child {
            page-break-after: auto;
          }
          .picking-header {
            margin-bottom: 6mm !important;
          }
          .picking-header h2 {
            font-size: 16pt !important;
          }
          .picking-header p {
            font-size: 11pt !important;
          }
          .picking-customers {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 4mm !important;
          }
          .customer-box {
            border: 2pt solid black !important;
            padding: 3mm !important;
            font-size: 10pt !important;
            width: calc(33.333% - 3mm) !important;
            box-sizing: border-box !important;
            page-break-inside: avoid;
          }
          .customer-box-header {
            border: 2pt solid black !important;
            background: white !important;
            color: black !important;
            padding: 2mm 3mm !important;
            margin: -3mm -3mm 3mm -3mm !important;
            display: flex !important;
            align-items: center !important;
            gap: 3mm !important;
            font-size: 11pt !important;
          }
          .route-badge {
            border: 2pt solid black !important;
            background: white !important;
            color: black !important;
            padding: 1mm 3mm !important;
            font-weight: 900 !important;
            font-size: 12pt !important;
          }
          .customer-code {
            font-family: monospace !important;
            font-weight: 900 !important;
            font-size: 14pt !important;
            color: black !important;
          }
          .customer-name {
            font-weight: 900 !important;
            font-size: 12pt !important;
            flex: 1 !important;
            color: black !important;
          }
          .comment-indicator {
            background: none !important;
            color: black !important;
            padding: 1mm 2mm !important;
            font-size: 13pt !important;
            font-weight: bold !important;
          }
          .proxy-indicator {
            background: none !important;
            color: black !important;
            border: 1px solid black !important;
            padding: 0.5mm 2mm !important;
            font-size: 10pt !important;
            font-weight: bold !important;
            border-radius: 2mm !important;
          }
          .customer-box-items {
            display: flex !important;
            flex-direction: column !important;
            gap: 1mm !important;
          }
          .item-row {
            display: flex !important;
            align-items: center !important;
            gap: 2mm !important;
            padding: 1mm 0 !important;
            border-bottom: 0.5pt solid #666 !important;
            font-size: 9pt !important;
          }
          .item-row:last-child {
            border-bottom: none !important;
          }
          .item-name {
            flex: 1 !important;
            font-weight: bold !important;
            font-size: 12pt !important;
          }
          .item-spec {
            color: black !important;
            width: 15mm !important;
            font-size: 10pt !important;
            font-weight: bold !important;
          }
          .item-qty {
            font-weight: bold !important;
            width: 12mm !important;
            text-align: right !important;
            font-size: 11pt !important;
          }
          .item-check {
            width: 5mm !important;
            text-align: center !important;
            font-size: 12pt !important;
          }
          
          /* ============================================ */
          /* ルート別配送一覧（横向き・大きめ表示）         */
          /* ============================================ */
          #route-orders-print {
            padding: 3mm;
            width: 100% !important;
            overflow: hidden !important;
          }
          #route-orders-print .print-header h2 {
            font-size: 18pt !important;
          }
          #route-orders-print .print-header p {
            font-size: 12pt !important;
          }
          #route-orders-print .route-cards-grid {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 4mm !important;
            width: 100% !important;
          }
          #route-orders-print .route-card {
            border: 2pt solid black !important;
            width: calc(33.333% - 3mm) !important;
            font-size: 11pt !important;
            page-break-inside: avoid;
            min-height: 20mm !important;
          }
          #route-orders-print .route-card-header {
            background: #ddd !important;
            padding: 3mm !important;
            border-bottom: 2pt solid black !important;
            display: flex !important;
            justify-content: space-between !important;
            font-weight: bold !important;
            font-size: 13pt !important;
          }
          #route-orders-print .route-card-body {
            padding: 3mm !important;
          }
          #route-orders-print .route-customer {
            padding: 2mm 0 !important;
            border-bottom: 0.5pt dotted #999 !important;
            font-size: 13pt !important;
          }
          #route-orders-print .route-customer:last-child {
            border-bottom: none !important;
          }
          #route-orders-print .route-customer .customer-name {
            font-size: 13pt !important;
            font-weight: normal !important;
          }
          #route-orders-print .route-customer .customer-code {
            font-size: 10pt !important;
            color: #333 !important;
          }
          #route-orders-print .route-summary {
            margin-top: 5mm !important;
            text-align: right !important;
            font-size: 12pt !important;
            font-weight: bold !important;
            width: 100% !important;
          }
          
          /* ============================================ */
          /* 仕入れ先別発注書（参照画像に準拠）            */
          /* ============================================ */
          .supplier-page-compact {
            page-break-after: always;
            padding: 10mm 10mm 0 10mm !important;
            position: relative !important;
            min-height: 270mm !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          .supplier-page-compact:last-child {
            page-break-after: auto;
          }
          /* コンテンツ部分 */
          .supplier-content {
            padding-bottom: 30mm !important;
          }
          /* ヘッダー */
          .supplier-header {
            text-align: center !important;
            margin-bottom: 8mm !important;
            padding-bottom: 0 !important;
          }
          .supplier-title {
            font-size: 28pt !important;
            font-weight: bold !important;
            text-decoration: underline !important;
            text-underline-offset: 4mm !important;
            margin-bottom: 5mm !important;
            letter-spacing: 0.3em !important;
          }
          .order-method-label {
            font-size: 25pt !important;
            text-decoration: none !important;
            letter-spacing: 0 !important;
            margin-left: 3mm !important;
          }
          .supplier-date {
            text-align: center !important;
            font-size: 16pt !important;
            margin-bottom: 5mm !important;
          }
          .supplier-name {
            text-align: left !important;
            font-size: 18pt !important;
            font-weight: bold !important;
            border-bottom: 2pt solid black !important;
            padding-bottom: 3mm !important;
            display: block !important;
            width: fit-content !important;
          }
          /* セクション */
          .supplier-section {
            margin-bottom: 5mm !important;
          }
          .section-label {
            font-size: 12pt !important;
            font-weight: bold !important;
            padding: 2mm 3mm !important;
            margin-bottom: 2mm !important;
            background: none !important;
            border-bottom: 1pt solid black !important;
          }
          .supplier-page-compact .supplier-table {
            font-size: 13pt !important;
            width: 100% !important;
          }
          .supplier-page-compact .supplier-table thead tr {
            background: none !important;
          }
          .supplier-page-compact .supplier-table th {
            background: none !important;
            border-bottom: 2pt solid black !important;
          }
          .supplier-page-compact .supplier-table td,
          .supplier-page-compact .supplier-table th {
            padding: 2mm 3mm !important;
            font-size: 13pt !important;
          }
          /* 合計列: 大きく中央揃え */
          .supplier-page-compact .supplier-table th:last-child,
          .supplier-page-compact .supplier-table td:last-child {
            text-align: center !important;
            font-size: 14pt !important;
            font-weight: bold !important;
          }
          .supplier-page-compact .supplier-table .col-origin-spec {
            font-size: 13pt !important;
            max-width: 40mm !important;
          }
          .supplier-page-compact .mt-1 {
            margin-top: 2mm !important;
            font-size: 13pt !important;
          }
          /* メモ欄 */
          .supplier-page-compact .memo-section {
            margin: 5mm 0 !important;
          }
          .supplier-page-compact .memo-section .section-label {
            font-size: 12pt !important;
          }
          .supplier-page-compact .memo-section .memo-line {
            height: 8mm !important;
            border-bottom: 0.5pt solid #999 !important;
          }
          /* フッター: ページ下部に固定 */
          .supplier-footer {
            position: absolute !important;
            bottom: 0 !important;
            left: 10mm !important;
            right: 10mm !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-end !important;
            border-top: 2pt solid black !important;
            padding-top: 3mm !important;
          }
          .supplier-footer-left {
            font-size: 13pt !important;
            line-height: 1.6 !important;
            font-weight: bold !important;
          }
          .delivery-type-mark {
            font-size: 36pt !important;
            font-weight: 900 !important;
            border: 3pt solid black !important;
            padding: 2mm 6mm !important;
            display: inline-block !important;
            line-height: 1 !important;
          }
          
          /* コメント確認 */
          #comments-print .print-header h2 {
            font-size: 18pt !important;
          }
          #comments-print table {
            font-size: 12pt !important;
          }
          
          /* 在庫対応 */
          #stock-print {
            padding: 5mm !important;
          }
          #stock-print .print-header h2 {
            font-size: 18pt !important;
          }
          #stock-print .print-header p {
            font-size: 13pt !important;
          }
          .stock-section {
            margin-bottom: 8mm !important;
          }
          .stock-section .section-label {
            font-size: 14pt !important;
          }
          #stock-print .supplier-table {
            font-size: 12pt !important;
          }
          #stock-print .supplier-table td,
          #stock-print .supplier-table th {
            padding: 2mm 3mm !important;
            font-size: 12pt !important;
          }
          
          /* 豊洲タスクリスト（A4縦1ページに収める） */
          #toyosu-print {
            padding: 10mm !important;
            width: 100% !important;
            max-height: 277mm !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
            page-break-inside: avoid !important;
          }
          #toyosu-print .print-header {
            text-align: center !important;
            margin-bottom: 5mm !important;
          }
          #toyosu-print .print-header h2 {
            font-size: 20pt !important;
            font-weight: bold !important;
            margin: 0 0 2mm 0 !important;
          }
          #toyosu-print .print-header p {
            font-size: 12pt !important;
            margin: 0 !important;
          }
          .toyosu-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 11pt !important;
            margin-top: 3mm !important;
            table-layout: fixed !important;
          }
          .toyosu-th {
            border: 1.5pt solid black !important;
            padding: 2mm 3mm !important;
            font-weight: bold !important;
            font-size: 11pt !important;
            text-align: left !important;
          }
          .toyosu-td {
            border: 1pt solid #666 !important;
            padding: 2mm 3mm !important;
            font-size: 13pt !important;
            word-break: break-all !important;
          }
          .toyosu-name {
            font-weight: bold !important;
            font-size: 13pt !important;
          }
          .toyosu-check {
            width: 12mm !important;
            text-align: center !important;
          }
          .toyosu-check-cell {
            text-align: center !important;
            font-size: 14pt !important;
          }
          .toyosu-memo {
            margin-top: 4mm !important;
            padding-top: 2mm !important;
          }
          .toyosu-memo .section-label {
            font-size: 11pt !important;
            border-bottom: 1pt solid black !important;
            padding-bottom: 1mm !important;
            margin-bottom: 2mm !important;
          }
          .toyosu-memo-item {
            font-size: 11pt !important;
            padding: 1mm 0 !important;
          }
          
          /* お米発注書 */
          .rice-page {
            page-break-after: always;
            padding: 10mm !important;
            position: relative !important;
          }
          .rice-page:last-child {
            page-break-after: auto;
          }
          .rice-header {
            text-align: center !important;
            margin-bottom: 8mm !important;
          }
          .rice-title {
            font-size: 24pt !important;
            font-weight: bold !important;
            text-decoration: underline !important;
            text-underline-offset: 3mm !important;
            margin-bottom: 4mm !important;
            letter-spacing: 0.2em !important;
          }
          .rice-title .order-method-label {
            font-size: 20pt !important;
            text-decoration: none !important;
            letter-spacing: 0 !important;
          }
          .rice-date {
            text-align: center !important;
            font-size: 14pt !important;
            margin-bottom: 4mm !important;
          }
          .rice-supplier-name {
            text-align: left !important;
            font-size: 16pt !important;
            font-weight: bold !important;
            border-bottom: 2pt solid black !important;
            padding-bottom: 2mm !important;
            display: block !important;
            width: fit-content !important;
          }
          .rice-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 12pt !important;
            margin-top: 4mm !important;
          }
          .rice-table th {
            border: 1.5pt solid black !important;
            padding: 2mm 3mm !important;
            font-weight: bold !important;
            text-align: left !important;
          }
          .rice-table td {
            border: 1pt solid #666 !important;
            padding: 2mm 3mm !important;
          }
          .rice-summary {
            margin-top: 8mm !important;
          }
          .rice-summary h3 {
            font-size: 13pt !important;
            font-weight: bold !important;
            border-bottom: 1pt solid black !important;
            padding-bottom: 2mm !important;
            margin-bottom: 2mm !important;
          }
          .rice-footer {
            margin-top: 10mm !important;
            padding-top: 3mm !important;
            border-top: 2pt solid black !important;
            font-size: 12pt !important;
            font-weight: bold !important;
          }
          
          /* 互換用 */
          .customer-page {
            page-break-after: always;
          }
          .customer-page:last-child {
            page-break-after: auto;
          }
          .page-break {
            display: none !important;
          }
        }
        
        @media screen {
          .print-area { margin-top: 2rem; }
          .picking-page {
            border: 2px dashed #10b981;
            padding: 1rem;
            margin-bottom: 2rem;
            border-radius: 0.5rem;
            background: #f0fdf4;
          }
          .picking-customers {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }
          .customer-box {
            border: 3px solid #1e293b;
            border-radius: 8px;
            padding: 8px;
            font-size: 13px;
            width: calc(33.333% - 7px);
            box-sizing: border-box;
            background: white;
          }
          .customer-box-header {
            border: 3px solid #1e293b;
            border-bottom: 3px solid #1e293b;
            background: #f8fafc;
            color: #1e293b;
            padding: 8px 12px;
            margin: -8px -8px 8px -8px;
            border-radius: 5px 5px 0 0;
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 14px;
          }
          .route-badge {
            border: 3px solid #1e293b;
            background: white;
            color: #1e293b;
            padding: 2px 10px;
            font-weight: 900;
            border-radius: 4px;
            font-size: 18px;
          }
          .customer-code {
            font-family: monospace;
            font-weight: 900;
            font-size: 16px;
            color: #1e293b;
          }
          .customer-name {
            font-weight: 900;
            font-size: 18px;
            flex: 1;
            color: #1e293b;
          }
          .proxy-indicator {
            border: 1px solid #1e293b;
            background: #fef3c7;
            color: #1e293b;
            padding: 1px 6px;
            font-weight: 700;
            border-radius: 3px;
            font-size: 11px;
            white-space: nowrap;
          }
          .customer-box-items {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }
          .item-row {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 6px 0;
            border-bottom: 1px solid #e2e8f0;
            font-size: 13px;
          }
          .item-row:last-child {
            border-bottom: none;
          }
          .item-name {
            flex: 1;
            font-weight: bold;
            font-size: 14px;
          }
          .item-spec {
            color: black;
            width: 80px;
            font-size: 12px;
            font-weight: bold;
          }
          .item-qty {
            font-weight: bold;
            width: 70px;
            text-align: right;
            color: #0f172a;
            font-size: 16px;
          }
          .item-check {
            width: 24px;
            text-align: center;
            font-size: 18px;
          }
          .customer-page {
            border: 2px dashed #cbd5e1;
            padding: 1.5rem;
            margin-bottom: 2rem;
            border-radius: 0.5rem;
          }
          .supplier-page-compact {
            border: 2px solid #3b82f6;
            padding: 1rem;
            margin-bottom: 1.5rem;
            border-radius: 0.5rem;
            background: white;
            position: relative;
          }
          .supplier-content {
            /* screen mode */
          }
          .supplier-header {
            text-align: center;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
          }
          .supplier-title {
            font-size: 1.5rem;
            font-weight: bold;
            text-decoration: underline;
            margin-bottom: 0.5rem;
            letter-spacing: 0.2em;
          }
          .order-method-label {
            font-size: 1.2rem;
            text-decoration: none;
            letter-spacing: 0;
            margin-left: 0.5rem;
          }
          .supplier-date {
            text-align: right;
            font-size: 0.9rem;
            color: #64748b;
            margin-bottom: 0.5rem;
          }
          .supplier-name {
            text-align: left;
            font-size: 1.25rem;
            font-weight: bold;
            border-bottom: 2px solid #1e293b;
            padding-bottom: 0.25rem;
            display: inline-block;
          }
          .supplier-section {
            margin-bottom: 1rem;
          }
          .section-label {
            font-size: 0.875rem;
            font-weight: bold;
            padding: 0.25rem 0.5rem;
            margin-bottom: 0.25rem;
            background: #e2e8f0;
          }
          .supplier-footer {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 1rem;
            padding-top: 0.5rem;
            border-top: 2px solid #334155;
          }
          .supplier-footer-left {
            font-size: 0.8rem;
            color: #475569;
            line-height: 1.6;
          }
          .delivery-type-mark {
            font-size: 24px;
            font-weight: 900;
            border: 3px solid #1e293b;
            padding: 4px 12px;
            display: inline-block;
          }
          .memo-section .memo-line {
            height: 24px;
            border-bottom: 1px solid #e2e8f0;
          }
          .memo-section .memo-line:last-child {
            border-bottom: none;
          }
          .page-break {
            height: 1px;
            background: #e2e8f0;
            margin: 2rem 0;
          }
          /* 豊洲リスト (screen) */
          .toyosu-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
            margin-top: 1rem;
          }
          .toyosu-th {
            border: 2px solid #1e293b;
            padding: 10px;
            font-weight: bold;
            text-align: left;
            background: #f1f5f9;
          }
          .toyosu-td {
            border: 1px solid #cbd5e1;
            padding: 10px;
          }
          .toyosu-name {
            font-weight: bold;
          }
          .toyosu-check {
            width: 60px;
            text-align: center;
          }
          .toyosu-check-cell {
            text-align: center;
            font-size: 18px;
          }
          .toyosu-memo {
            margin-top: 1.5rem;
            padding-top: 0.5rem;
          }
          .toyosu-memo .section-label {
            font-size: 14px;
            font-weight: bold;
            border-bottom: 2px solid #334155;
            padding-bottom: 0.25rem;
            margin-bottom: 0.5rem;
          }
          .toyosu-memo-item {
            font-size: 14px;
            padding: 4px 0;
          }
          /* お米 (screen) */
          .rice-page {
            border: 2px solid #92400e;
            padding: 1rem;
            margin-bottom: 1.5rem;
            border-radius: 0.5rem;
            background: white;
          }
          .rice-header { text-align: center; margin-bottom: 1rem; }
          .rice-title { font-size: 1.5rem; font-weight: bold; text-decoration: underline; letter-spacing: 0.2em; }
          .rice-title .order-method-label { font-size: 1.2rem; text-decoration: none; letter-spacing: 0; margin-left: 0.5rem; }
          .rice-date { font-size: 0.9rem; color: #64748b; margin: 0.5rem 0; }
          .rice-supplier-name { font-size: 1.25rem; font-weight: bold; border-bottom: 2px solid #1e293b; padding-bottom: 0.25rem; display: inline-block; }
          .rice-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 0.5rem; }
          .rice-table th { border: 2px solid #1e293b; padding: 8px; font-weight: bold; text-align: left; background: #fef3c7; }
          .rice-table td { border: 1px solid #cbd5e1; padding: 8px; }
          .rice-summary { margin-top: 1rem; }
          .rice-summary h3 { font-size: 14px; font-weight: bold; border-bottom: 2px solid #334155; padding-bottom: 0.25rem; margin-bottom: 0.5rem; }
          .rice-footer { margin-top: 1rem; padding-top: 0.5rem; border-top: 2px solid #334155; font-weight: bold; font-size: 14px; }
        }
      `}</style>
    </div>
  );
};
